import { fetcher } from '@commenda/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduledAgentsKeys } from '../scheduled-agents/keys';
import { CONNECTIONS_ROUTE, connectionsKeys } from './keys';
import { ICreateConnectionResult } from './types';

const readErrorMessage = async (
  response: Response,
  fallback: string
): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string | string[];
  };
  const message = Array.isArray(body.message) ? body.message[0] : body.message;

  return message || fallback;
};

export const useCreateConnection = () =>
  useMutation({
    mutationFn: async ({
      toolkitSlug,
    }: {
      toolkitSlug: string;
    }): Promise<ICreateConnectionResult> => {
      const response = await fetcher(CONNECTIONS_ROUTE, {
        method: 'POST',
        body: { toolkitSlug },
      });
      if (!response.ok) {
        throw new Error(
          await readErrorMessage(response, 'Could not start the connection')
        );
      }

      return (await response.json()) as ICreateConnectionResult;
    },
  });

export const useDeleteConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectedAccountId,
    }: {
      connectedAccountId: string;
    }): Promise<void> => {
      const response = await fetcher(
        `${CONNECTIONS_ROUTE}/${connectedAccountId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(
          await readErrorMessage(response, 'Could not disconnect this app')
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [connectionsKeys.MINE] });
      queryClient.invalidateQueries({ queryKey: [connectionsKeys.TOOLKITS] });
      // The agent form caches channel availability for five minutes; a disconnected app must drop out of it now.
      queryClient.invalidateQueries({
        queryKey: scheduledAgentsKeys.notificationChannelsAll(),
      });
      queryClient.invalidateQueries({
        queryKey: scheduledAgentsKeys.notificationTargetsAll(),
      });
    },
  });
};
